/**
 * GuaranteeCalcEditor.tsx
 *
 * 保証協会請求書の構造化入力エディタ（fee-schedules 設計書 §1）。
 * 1債務者=1行で移転金額・個数を入力すると computeGuarantee() でライブ計算する。
 * 消費税・源泉所得税（1万円控除）はブロック（債務者）ごとに端数処理される点が
 * 他書類との最大の違い（business-logic.md §2）。
 *
 * - editable=true: schedule 必須。債務者行の追加/削除と仮払金額を編集できる
 * - editable=false: result（発行済みは calc_result_json スナップショット）のみから
 *   読み取り専用で描画する
 */
import {
  computeGuarantee,
  formatJPY,
  type GuaranteeComputedBlock,
  type GuaranteeDebtor,
  type GuaranteeInput,
  type GuaranteeResult,
  type GuaranteeSchedule,
} from "@juro/shared";
import { emptyGuaranteeDebtor } from "../../lib/feeCalcDefaults.js";
import { btnStyle } from "../../App.js";

const thStyle: React.CSSProperties = { padding: "6px 6px", textAlign: "right", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap", fontSize: 11 };
const tdStyle: React.CSSProperties = { padding: "4px 6px", whiteSpace: "nowrap" };
const inputStyle: React.CSSProperties = { padding: "4px 6px", border: "1px solid #cbd5e1", borderRadius: 5, fontSize: 12 };
const numInputStyle: React.CSSProperties = { ...inputStyle, width: 72, textAlign: "right" };

function num(v: string): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** 1債務者だけで計算し、その行の計算値を得る（全項目0の行は null） */
function computeDebtor(schedule: GuaranteeSchedule, d: GuaranteeDebtor): GuaranteeComputedBlock | null {
  const res = computeGuarantee(schedule, { kind: "guarantee", debtors: [d], advance_paid_amount: 0 });
  return res.blocks[0] ?? null;
}

// ── 合計表示（編集・読み取り共通） ──────────────────────────────────────────
function TotalsView({ result }: { result: GuaranteeResult }) {
  const mainRows: { label: string; value: number; emphasis?: boolean }[] = [
    { label: "総合計（Σ合計）", value: result.grand_total },
    { label: "源泉所得税 合計", value: result.withholding_total },
    { label: "請求金額（総合計−源泉）", value: result.billed_total },
    { label: "仮払金額", value: result.advance_paid_amount },
    { label: "振込金額（請求金額−仮払）", value: result.transfer_net_total, emphasis: true },
  ];
  const stampRows: { label: string; value: number }[] = [
    { label: "① 閲覧印紙（個数加算）", value: result.inspection_stamp_total },
    { label: "② 謄本印紙", value: result.transcript_stamp_total },
    { label: "③ 印紙計（①＋②）", value: result.stamp_total },
    { label: "④ 登録免許税", value: result.registration_tax_total },
    { label: "⑤ 合計（③＋④）", value: result.tax_breakdown_total },
  ];
  return (
    <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>印紙・登録免許税内訳</div>
        <table style={{ minWidth: 240, borderCollapse: "collapse", fontSize: 12 }}>
          <tbody>
            {stampRows.map((r) => (
              <tr key={r.label}>
                <td style={{ padding: "4px 8px", borderBottom: "1px solid #e2e8f0", color: "#64748b" }}>{r.label}</td>
                <td style={{ padding: "4px 8px", borderBottom: "1px solid #e2e8f0", textAlign: "right", color: "#334155", fontWeight: 600 }}>
                  {formatJPY(r.value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <table style={{ minWidth: 320, borderCollapse: "collapse", fontSize: 13, alignSelf: "flex-start" }}>
        <tbody>
          {mainRows.map((r) => (
            <tr key={r.label}>
              <td style={{ padding: "6px 10px", borderBottom: "1px solid #e2e8f0", color: "#475569", fontWeight: r.emphasis ? 800 : 600 }}>
                {r.label}
              </td>
              <td style={{ padding: "6px 10px", borderBottom: "1px solid #e2e8f0", textAlign: "right", color: r.emphasis ? "#0f766e" : "#0f172a", fontWeight: r.emphasis ? 900 : 700 }}>
                {formatJPY(r.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const COMPUTED_COLS: { label: string; pick: (b: GuaranteeComputedBlock) => number }[] = [
  { label: "登録免許税", pick: (b) => b.registration_tax },
  { label: "報酬計",     pick: (b) => b.fee_total },
  { label: "税計",       pick: (b) => b.tax_total },
  { label: "消費税",     pick: (b) => b.consumption_tax },
  { label: "源泉",       pick: (b) => b.withholding_tax },
  { label: "請求額",     pick: (b) => b.billed_total },
];

// ── 読み取り専用表示（発行済み: calc_result スナップショットのみから描画） ──
function ReadOnlyView({ result }: { result: GuaranteeResult | null }) {
  if (!result) {
    return <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>計算結果がありません</p>;
  }
  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 720 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              <th style={{ ...thStyle, textAlign: "left" }}>債務者名</th>
              <th style={{ ...thStyle, textAlign: "left" }}>顧客番号</th>
              <th style={thStyle}>移転金額</th>
              <th style={thStyle}>基本報酬</th>
              <th style={thStyle}>閲覧</th>
              <th style={thStyle}>謄本</th>
              <th style={thStyle}>日当・旅費</th>
              {COMPUTED_COLS.map((c) => <th key={c.label} style={thStyle}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {result.blocks.map((b, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{b.name || "—"}</td>
                <td style={tdStyle}>{b.customer_number || "—"}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatJPY(b.transfer_amount)}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatJPY(b.base_fee)}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatJPY(b.inspection_amount)}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatJPY(b.transcript_amount)}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>{formatJPY(b.per_diem + b.travel)}</td>
                {COMPUTED_COLS.map((c) => (
                  <td key={c.label} style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{formatJPY(c.pick(b))}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TotalsView result={result} />
    </div>
  );
}

// ── 本体 ────────────────────────────────────────────────────────────────────
export function GuaranteeCalcEditor({
  schedule, input, result, onChange, editable,
}: {
  /** 現在の料金表（編集時は必須。読み取り専用表示では未使用） */
  schedule: GuaranteeSchedule | null;
  input:    GuaranteeInput;
  /** draft はライブ計算値、issued は calc_result スナップショット */
  result:   GuaranteeResult | null;
  onChange: (input: GuaranteeInput) => void;
  editable: boolean;
}) {
  if (!editable) return <ReadOnlyView result={result} />;
  if (!schedule) {
    return <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>料金表を読み込み中…</p>;
  }

  function updateDebtor(index: number, patch: Partial<GuaranteeDebtor>) {
    onChange({ ...input, debtors: input.debtors.map((d, i) => (i === index ? { ...d, ...patch } : d)) });
  }

  function addDebtor() {
    onChange({ ...input, debtors: [...input.debtors, emptyGuaranteeDebtor()] });
  }

  function removeDebtor(index: number) {
    onChange({ ...input, debtors: input.debtors.filter((_, i) => i !== index) });
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
        債務者ごとに1行で入力します。消費税・源泉所得税（1万円控除）は債務者ごとに計算されます。
        単価（基本報酬 {formatJPY(schedule.base_fee)}・閲覧 {formatJPY(schedule.inspection_unit_price)}・謄本 {formatJPY(schedule.transcript_unit_price)} 等）は料金表から自動適用されます。
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 1080 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              <th style={{ ...thStyle, textAlign: "left" }}>債務者名</th>
              <th style={{ ...thStyle, textAlign: "left" }}>顧客番号</th>
              <th style={thStyle}>移転金額</th>
              <th style={thStyle}>基本報酬</th>
              <th style={thStyle}>閲覧 個数</th>
              <th style={thStyle}>謄本 個数</th>
              <th style={thStyle}>個数加算(税)</th>
              <th style={thStyle}>謄本(税) 個数</th>
              <th style={thStyle}>日当</th>
              <th style={thStyle}>旅費</th>
              {COMPUTED_COLS.map((c) => <th key={c.label} style={thStyle}>{c.label}</th>)}
              <th style={{ ...thStyle, width: 24 }} />
            </tr>
          </thead>
          <tbody>
            {input.debtors.map((d, index) => {
              const b = computeDebtor(schedule, d);
              return (
                <tr key={index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    <input type="text" value={d.name} placeholder="債務者名"
                      onChange={(e) => updateDebtor(index, { name: e.target.value })}
                      style={{ ...inputStyle, width: 120 }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="text" value={d.customer_number ?? ""}
                      onChange={(e) => updateDebtor(index, { customer_number: e.target.value || undefined })}
                      style={{ ...inputStyle, width: 80 }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} value={d.transfer_amount}
                      onChange={(e) => updateDebtor(index, { transfer_amount: Math.max(0, Math.trunc(num(e.target.value))) })}
                      style={{ ...numInputStyle, width: 100 }} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <input type="checkbox" checked={d.has_base_fee}
                      onChange={(e) => updateDebtor(index, { has_base_fee: e.target.checked })}
                      style={{ cursor: "pointer" }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} value={d.inspection_count}
                      onChange={(e) => updateDebtor(index, { inspection_count: Math.max(0, Math.trunc(num(e.target.value))) })}
                      style={{ ...numInputStyle, width: 56 }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} value={d.transcript_count}
                      onChange={(e) => updateDebtor(index, { transcript_count: Math.max(0, Math.trunc(num(e.target.value))) })}
                      style={{ ...numInputStyle, width: 56 }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} value={d.count_surcharge}
                      onChange={(e) => updateDebtor(index, { count_surcharge: Math.max(0, Math.trunc(num(e.target.value))) })}
                      style={{ ...numInputStyle, width: 56 }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} value={d.transcript_tax_count}
                      onChange={(e) => updateDebtor(index, { transcript_tax_count: Math.max(0, Math.trunc(num(e.target.value))) })}
                      style={{ ...numInputStyle, width: 56 }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} value={d.per_diem}
                      onChange={(e) => updateDebtor(index, { per_diem: Math.max(0, Math.trunc(num(e.target.value))) })}
                      style={{ ...numInputStyle, width: 72 }} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} value={d.travel}
                      onChange={(e) => updateDebtor(index, { travel: Math.max(0, Math.trunc(num(e.target.value))) })}
                      style={{ ...numInputStyle, width: 72 }} />
                  </td>
                  {COMPUTED_COLS.map((c) => (
                    <td key={c.label} style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#334155" }}>
                      {b ? formatJPY(c.pick(b)) : "—"}
                    </td>
                  ))}
                  <td style={{ ...tdStyle, width: 24 }}>
                    {input.debtors.length > 1 && (
                      <button onClick={() => removeDebtor(index)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16, padding: "0 4px" }}>×</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={addDebtor} style={{ ...btnStyle("ghost"), fontSize: 13 }}>
          + 債務者を追加
        </button>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>仮払金額</span>
          <input type="number" min={0} value={input.advance_paid_amount}
            onChange={(e) => onChange({ ...input, advance_paid_amount: Math.max(0, Math.trunc(num(e.target.value))) })}
            style={{ ...numInputStyle, width: 120 }} />
        </label>
      </div>
      {result && <TotalsView result={result} />}
    </div>
  );
}
